import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';

import messages from './messages/messages';
import prompts from './prompts/prompts';
import multiFactorAuthentication from './multi-factor-authentication/multi-factor-authentication';
import landingPageBuilder from '@/stores/landing-page-builder/landing-page-builder';
import landingPageFormBuilder from '@/stores/landing-page-builder/landing-page-form-builder';
import documentBuilder from '@/stores/document-builder/document-builder';
import partnerSurveyBuilder from '@/stores/partner-survey-builder/partner-survey-builder';
import notifications from './notifications/notifications';
import currency from './currency/currency';

import { api } from '../scripts/api';
import { set } from 'vue-analytics';

import jwtDecode from 'jwt-decode';
import { CurrencyService } from '@/scripts/services/company-account-settings/currency-service';
import { woli } from '@/stores/woli/woli-module';
import { theme } from '@/stores/theme/theme-module';
import { appointmentRequests } from '@/stores/appointment-requests/appointment-requests-module';

Vue.use(Vuex);

const defaultActiveState = () => ({
    isActive: null,
    isConnectWise: null,
    adminEmail: null,
});

const defaultUserClientState = () => ({
    promoName: null,
    firstVendorConnection: null,
    primaryVendorConnection: null,
    signupSource: null,
});

const store = new Vuex.Store({
    state: {
        isLoggedIn: false,
        loginDestinationRoute: null,
        user: {
            id: null,
            clientId: null,
            profileImageUrl: null,
            roles: [],
            organizationId: null,
            masterAgencyId: null,
            agencyId: null,
            yearsOfExperience: null,
            title: null,
            email: null,
            vendorId: null,
            firstName: null,
            lastName: null,
            linkedinUrl: null,
            lastCheckIn: null,
            paidAccount: false,
            activeState: defaultActiveState(),
            client: defaultUserClientState(),
        },

        context: {
            client: {
                managesOwnIntegrations: null,
                id: null,
                clientName: null,
                logoImageUrl: null,
                isIntegrateComplete: null,
                isListComplete: null,
                isTeamComplete: null,
                isKpiComplete: null,
                isBillingComplete: null,
                managesSubscription: null,
                percentage: null,
                organizationId: null,
                isActive: null,
                includeViewInBrowserLeads: null,
                monthlyLeadQuota: null,
                monthlyRevenueQuota: null,
                averageMsa: null,
                appointmentPercent: null,
                proposalPercent: null,
                closePercent: null,
                isAccountSetUp: null,
                isAccepted: null,
                underReview: null,
                promoId: null,
                enabledFeatures: null,
                createdAt: null
            },
            vendor: {
                createdAt: null,
                id: null,
                isActive: null,
                logoImageUrl: null,
                name: null,
                organizationId: null,
                updatedAt: null
            },
            organization: {},
            agency: {
                id: null,
                name: null,
                logoImageUrl: null,
                created: null,
                updated: null,
                isActive: null
            },
            masterAgency: {
                id: null,
                name: null,
                logoImageUrl: null,
                includeAllPartnerData: null,
                created: null,
                updated: null
            },
            account: {
                id: null,
                name: null
            },
        },

        messages: messages,
        prompts: prompts,
        multiFactorAuthentication: multiFactorAuthentication,
        landingPageBuilder: landingPageBuilder,
        landingPageFormBuilder: landingPageFormBuilder,
        documentBuilder: documentBuilder,
        partnerSurveyBuilder: partnerSurveyBuilder,
        notifications: notifications,
        currency: currency
    },

    getters: {
        isOrganizationContext(state, getters) {
            return getters.isOrganizationUser && !state.context.client.id && !state.context.agency.id && !state.context.masterAgency.id;
        },

        isAnalyticsOnlyAgency(state) {
            return state.context.agency.id === 113 || state.user.agencyId === 113; // Microsoft
        },

        isAdmin(state) {
            return state.user.roles.includes('Admin');
        },

        isClientUser(state) {
            return state.user.roles.includes('Client');
        },

        isParentUser(state, getters) {
            return getters.isOrganizationUser || getters.isVendorUser || getters.isAgencyUser || getters.isMasterAgencyUser;
        },

        isOrganizationUser(state) {
            return state.user.roles.includes('Organization');
        },

        isVendorUser(state) {
            return state.user.roles.includes('Vendor');
        },

        isMasterAgencyUser(state) {
            return state.user.roles.includes('MasterAgency');
        },

        isAgencyUser(state) {
            return state.user.roles.includes('Agency');
        },

        isMarketer(state) {
            return state.user.roles.includes('Marketer');
        },

        isSalesperson(state) {
            return state.user.roles.includes('Salesperson');
        },

        isMDFManager(state) {
            return state.user.roles.includes('MDFManager')
        },

        isSalesAdmin(state) {
            return state.user.roles.includes('Admin') && state.user.roles.includes('Salesperson');
        },

        isReadOnlyUser(state) {
            return state.user.roles.includes('ReadOnly');
        },

        isDocumentSenderUser(state) {
            return state.user.roles.includes('SendDocuments');
        },

        isAgencyAdmin(state) {
            return state.user.roles.includes('AgencyAdmin');
        },

        isMarketingCoordinator(state) {
            return state.user.roles.includes('MarketingCoordinator');
        },

        isAccountsManager(state) {
            return state.user.roles.includes('AccountsManager');
        },

        isBillingPaymentUser(state) {
            return state.user.roles.includes('BillingPayment');
        },

        baseUrl(state, getters) {
            return getters.routeBaseUrl || getters.agencyBaseUrl || getters.masterAgencyBaseUrl;
        },

        routeBaseUrl(state, getters) {
            if (getters.isParentUser && state.context.client.id) {
                return `/Clients/${state.context.client.id}`;
            }

            return '';
        },

        agencyBaseUrl(state, getters) {
            if (getters.isOrganizationUser || getters.isMasterAgencyUser) {
                if (state.context.agency.id) {
                    return `/Agencies/${state.context.agency.id}`;
                }
            }

            return '';
        },

        masterAgencyBaseUrl(state, getters) {
            if (getters.isOrganizationUser && state.context.masterAgency.id) {
                return `/MasterAgencies/${state.context.masterAgency.id}`;
            }

            return '';
        },

        lastCheckIn(state) {
            return state.user.lastCheckIn;
        }
    },

    mutations: {
        decodeJwt(state) {
            var token = localStorage.getItem('jwt');
            var decodedToken = jwtDecode(token);

            state.user.id = decodedToken.userId;
            var roles = decodedToken.roles;
            state.user.roles = roles.map(role => {
                return role.Name;
            });
        },
        
        setUser(state, payload) {
            state.user.email = payload.Email || '';
            state.user.firstName = payload.FirstName || '';
            state.user.lastName = payload.LastName || '';
            state.user.linkedinUrl = payload.LinkedinUrl || '';
            state.user.profileImageUrl = payload.ProfileImageUrl || '';
            state.user.title = payload.Title || '';
            state.user.yearsOfExperience = payload.YearsOfExperience || null;
            state.user.lastCheckIn = new Date();
            state.user.activeState = {
                isActive: payload.IsActive,
                isConnectWise: null,
                adminEmail: null,
            };

            state.user.clientId = payload.ClientId;
            state.user.organizationId = payload.OrganizationId || null;
            state.user.vendorId = payload.VendorId || null;
            state.user.agencyId = payload.AgencyId || null;
            state.user.masterAgencyId = payload.MasterAgencyId || null;

            state.context.client.isAccountSetUp = payload.Client ? payload.Client.IsAccountSetUp : null;
            state.context.client.isActive = payload.Client ? payload.Client.IsActive : null;

            if (payload.Client) {
                state.user.paidAccount = payload.Client.IsActive
                    && payload.Client.ManagesSubscription
                    && payload.Client.Promo.BillingCycle > 0;
                state.user.client.promoName = payload.Client.Promo.Name;
                if (payload.Client.Accounts && payload.Client.Accounts.length > 0) {
                    state.user.client.signupSource = payload.Client.Accounts[0].SignupSource || null;
                }
            }
        },

        setVendorConnections(state, agencyClients) {
            var firstAgencyClient = agencyClients.reduce((earliestAc, currentAc) => {
                return new Date(currentAc.createdAt) < new Date(earliestAc.createdAt)
                    ? currentAc
                    : earliestAc;
            });
            if (firstAgencyClient && firstAgencyClient.agency) {
                state.user.client.firstVendorConnection = firstAgencyClient.agency.name;
            }

            const vendorPriority = [
                "ConnectWise",
                "Microsoft + ConnectWise",
                "CharTec",
                "Bitdefender",
                "Sophos",
                "SentinelOne",
                "TD Synnex",
            ];
            agencyClients.sort((a, b) => {
                const indexA = vendorPriority.indexOf(a.agency.name);
                const indexB = vendorPriority.indexOf(b.agency.name);
                const fallbackIndex = vendorPriority.length;
                return (indexA === -1 ? fallbackIndex : indexA) - (indexB === -1 ? fallbackIndex : indexB);
            });
            const matchingAgencyClient = agencyClients.find(client =>
                vendorPriority.includes(client.agency.name)
            );
            if (matchingAgencyClient && matchingAgencyClient.agency) {
                state.user.client.primaryVendorConnection = matchingAgencyClient.agency.name;
            }
        },

        setIsAccountSetUp(state, payload) {
            state.context.client.isAccountSetUp = payload;
        },

        setUserIsActive(state, payload) {
            state.user.activeState.isActive = payload;
        },

        setUserAdminEmail(state, payload) {
            state.user.activeState.adminEmail = payload;
        },

        setUserIsConnectWise(state, payload) {
            state.user.activeState.isConnectWise = payload;
        },

        hydrateClientContext(state, payload) {
            state.context.client.id = payload.Id;
            state.context.client.promoId = payload.PromoId;
            state.context.client.managesOwnIntegrations = payload.ManagesOwnIntegrations;
            state.context.client.clientName = payload.Name;
            state.context.client.logoImageUrl = payload.LogoImageUrl || '@/assets/images/company-avatar.png';
            state.context.client.isIntegrateComplete = payload.IsIntegrationStepComplete;
            state.context.client.isListComplete = payload.IsListStepComplete;
            state.context.client.isTeamComplete = payload.IsTeamStepComplete;
            state.context.client.isKpiComplete = payload.IsKpiStepComplete;
            state.context.client.isBillingComplete = payload.IsBillingStepComplete;
            state.context.client.managesSubscription = payload.ManagesSubscription;
            state.context.client.percentage = 0;
            state.context.client.organizationId = payload.OrganizationId;
            state.context.client.isActive = payload.IsActive;
            state.context.client.includeViewInBrowserLeads = payload.IncludeViewInBrowserLeads;
            state.context.client.monthlyLeadQuota = payload.MonthlyLeadQuota;
            state.context.client.monthlyRevenueQuota = payload.MonthlyRevenueQuota;
            state.context.client.averageMsa = payload.AverageMsa;
            state.context.client.appointmentPercent = payload.AppointmentPercent;
            state.context.client.proposalPercent = payload.ProposalPercent;
            state.context.client.closePercent = payload.ClosePercent;
            state.context.client.underReview = payload.UnderReview;
            state.context.client.enabledFeatures = payload.Promo.FeatureToggles.map(toggle => {
                return {
                    id: toggle.FeatureId,
                    enabled: toggle.Enabled
                };
            }).filter(feature => feature.enabled);
            state.context.client.createdAt = payload.CreatedAt;
        },

        hydrateAccountContext(state, payload) {
            state.context.account.id = payload.Id;
            state.context.account.name = payload.Name;
        },

        hydrateMasterAgencyContext(state, payload) {
            state.context.masterAgency.id = payload.Id;
            state.context.masterAgency.name = payload.Name;
            state.context.masterAgency.logoImageUrl = payload.LogoImageUrl;
            state.context.masterAgency.includeAllPartnerData = payload.IncludeAllPartnerData;
            state.context.masterAgency.created = payload.CreatedAt;
            state.context.masterAgency.updated = payload.UpdatedAt;
        },

        hydrateAgencyContext(state, payload) {
            state.context.agency.id = payload.Id;
            state.context.agency.name = payload.Name;
            state.context.agency.logoImageUrl = payload.LogoImageUrl;
            state.context.agency.created = payload.CreatedAt;
            state.context.agency.updated = payload.UpdatedAt;
            state.context.agency.isActive = payload.IsActive;
        },

        setManagesSubscription(state, payload) {
            state.context.client.managesSubscription = payload;
        },

        calculateProgress(state) {
            var total = 5;
            var complete = 0;

            if (state.context.client.isIntegrateComplete) {
                complete += 1;
            }

            if (state.context.client.isListComplete) {
                complete += 1;
            }

            if (state.context.client.isTeamComplete) {
                complete += 1;
            }

            if (state.context.client.isKpiComplete) {
                complete += 1;
            }

            if (state.context.client.isBillingComplete) {
                complete += 1;
            }

            state.context.client.percentage = (complete / total) * 100;
        },

        clearUser(state) {
            state.user.id = null;
            state.user.clientId = null;
            state.user.profileImageUrl = null;
            state.user.roles = [];
            state.user.organizationId = null;
            state.user.masterAgencyId = null;
            state.user.agencyId = null;
            state.user.yearsOfExperience = null;
            state.user.title = null;
            state.user.email = null;
            state.user.vendorId = null;
            state.user.firstName = null;
            state.user.lastName = null;
            state.user.linkedinUrl = null;
            state.user.linkedinUrl = null;
            state.user.lastCheckIn = null;
            state.user.paidAccount = false;
            state.user.activeState = defaultActiveState();
            state.user.client = defaultUserClientState();

            localStorage.jwt = '';
            localStorage.refresh_token = '';
            localStorage.lastActiveMainNavItem = '';
        },

        clearAccountPrivacy() {
            const intervalId = sessionStorage.getItem('privacy_interval_id');
            if (intervalId) {
                clearInterval(intervalId);
            };
            sessionStorage.removeItem('privacy_session_id');
            sessionStorage.removeItem('privacy_interval_id');
            sessionStorage.removeItem('privacy_last_check_date');
        },

        clearClientContext(state) {
            state.context.client.id = null;
            state.context.client.managesOwnIntegrations = null;
            state.context.client.clientName = null;
            state.context.client.logoImageUrl = null;
            state.context.client.isIntegrateComplete = null;
            state.context.client.isListComplete = null;
            state.context.client.isTeamComplete = null;
            state.context.client.isKpiComplete = null;
            state.context.client.isBillingComplete = null;
            state.context.client.managesSubscription = null;
            state.context.client.percentage = null;
            state.context.client.organizationId = null;
            state.context.client.isActive = null;
            state.context.client.includeViewInBrowserLeads = null;
            state.context.client.monthlyLeadQuota = null;
            state.context.client.monthlyRevenueQuota = null;
            state.context.client.averageMsa = null;
            state.context.client.appointmentPercent = null;
            state.context.client.proposalPercent = null;
            state.context.client.closePercent = null;
            state.context.client.underReview = null;
            state.context.client.enabledFeatures = null;
            state.context.client.promoId = null;
            state.context.client.createdAt = null;
        },

        clearAgencyContext(state) {
            state.context.agency.id = null;
            state.context.agency.name = null;
            state.context.agency.logoImageUrl = null;
            state.context.agency.created = null;
            state.context.agency.updated = null;
            state.context.agency.isActive = null;
        },

        clearMasterAgencyContext(state) {
            state.context.masterAgency.id = null;
            state.context.masterAgency.name = null;
            state.context.masterAgency.logoImageUrl = null;
            state.context.masterAgency.includeAllPartnerData = null;
            state.context.masterAgency.created = null;
            state.context.masterAgency.updated = null;
        },

        clearVendorContext(state) {
            state.context.vendor.createdAt = null;
            state.context.vendor.id = null;
            state.context.vendor.isActive = null;
            state.context.vendor.logoImageUrl = null;
            state.context.vendor.name = null;
            state.context.vendor.organizationId = null;
            state.context.vendor.updatedAt = null;
        },

        clearAccountContext(state) {
            state.context.account.id = null;
            state.context.account.name = null;
        },

        setLoginDestinationRoute(state, route) {
            const disallowedPrefixes = ['/Account/Login', '/Unauthorized', '/404'];
            if (route
                && typeof route === 'string'
                && route.startsWith('/')
                && !disallowedPrefixes.some(prefix => route.toLowerCase().startsWith(prefix.toLowerCase()))
            ) {
                state.loginDestinationRoute = route;
            }
        },

        clearLoginDestinationRoute(state) {
            state.loginDestinationRoute = null;
        },

        setLoggedInStatus(state, isLoggedIn) {
            state.isLoggedIn = isLoggedIn;
        },

        updateClientLogo(state, payload) {
            state.context.client.logoImageUrl = payload;
        },

        updateAccountLogo(state, payload) {
            state.context[payload.accountType].logoImageUrl = payload.logo;
        },

        updateNewNotification(state, payload) {
            state.notifications.newNotification = payload;
        },

        updateBellCount(state, payload) {
            state.notifications.bell = payload;
        },

        updateNotificationPreferences(state, payload) {
            state.notifications.isInAppSoundEnabled = payload.IsInAppSoundEnabled || false;
        },

        setCurrenciesLoading(state, payload) {
            state.currency.loadingCurrencies = payload;
        },

        updateCurrencies(state, payload) {
            state.currency.currencies = payload;
        },
    },

    actions: {
        login({ dispatch }, credentials) {
            return new Promise((resolve, reject) => {
                api.post('/ms/auth/v1/auth/login/', credentials)
                    .then(response => {
                        localStorage.setItem('jwt', response.data.data.token);
                        localStorage.setItem('refresh_token', response.data.data.refresh_token.token);
                        dispatch('hydrateUser')
                            .then(resolve)
                            .catch(reject);
                    })
                    .catch(reject);
            });
        },

        loginViaTokens({ dispatch }, { refreshToken, jwt }) {
            return new Promise((resolve, reject) => {
                localStorage.setItem('jwt', jwt);
                localStorage.setItem('refresh_token', refreshToken);

                dispatch('hydrateUser')
                    .then(resolve)
                    .catch(reject);
            });
        },

        hydrateUser({ dispatch, commit }) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = `Bearer ${localStorage.jwt}`;

                commit('decodeJwt');

                dispatch('getUser')
                    .then(() => {
                        return Promise.all([
                            dispatch('theme/loadTheme'),
                            dispatch('woli/loadUserConfig'),
                        ]);
                    })
                    .then(() => {
                        return dispatch('updateIntercom');
                    })
                    .then(() => {
                        return dispatch('setGoogleAnalyticsInfo');
                    })
                    .then(resolve)
                    .catch(reject);
            });
        },

        getUser({ commit, state }) {
            return new Promise((resolve, reject) => {
                api.get(`/service/Users?$filter=Id eq '${state.user.id}'&$expand=Client($expand=Promo($select=BasePrice,BillingCycle,Name),Accounts($select=SignupSource)),Vendor,Agency`)
                    .then(response => {
                        commit('setUser', response.data.value[0]);
                        commit('setLoggedInStatus', true);
                        resolve();
                    })
                    .then(() => {
                        if (state.user.clientId) {
                            return api.get(`/service/AgencyClients?$filter=ClientId eq ${state.user.clientId} and IsAccepted eq true&$select=AgencyId,CreatedAt&$expand=Agency($select=Name)`)
                                .then((response) => {
                                    const agencyClients = response.data.value.map((agencyClient) => {
                                        return {
                                            agency: {
                                                id: agencyClient.AgencyId,
                                                name: agencyClient.Agency.Name,
                                            },
                                            createdAt: agencyClient.CreatedAt,
                                        };
                                    })
                                    if (agencyClients.length > 0) {
                                        commit('setVendorConnections', agencyClients);
                                    }
                                })
                                .catch((error) => {
                                    this.$helpers.showFailNotification(
                                        'Failed to load primary vendor connection for Intercom during login. Proceeding with login.',
                                        { error, enable: false },
                                    );
                                });
                        }
                        resolve();
                    })
                    .catch(reject);
            });
        },

        setGoogleAnalyticsInfo({ state }) {
            return new Promise((resolve) => {
                set('userId', state.user.id || null);

                if (state.user.paidAccount) {
                    set('dimension1', 'Paying User');
                } else {
                    set('dimension1', 'Free User');
                }
                ; //dimension1 = "isActiveUserPaying" in Google Analytics

                if (state.user.clientId) {
                    set('dimension2', 'Client');
                    set('dimension3', state.user.clientId);
                } else if (state.user.agencyId) {
                    set('dimension2', 'Agency');
                    set('dimension4', state.user.agencyId);
                } else if (state.user.masterAgencyId) {
                    set('dimension2', 'Master Agency');
                    set('dimension5', state.user.masterAgencyId);
                } else {
                    set('dimension2', 'Organization');
                }
                ; //dimension2 = "userAccountType" in Google Analytics

                resolve();
            });
        },

        updateIntercom({ state }) {
            return new Promise((resolve) => {
                var accountType = '';

                if (state.user.clientId) {
                    accountType = 'Client';
                } else if (state.user.agencyId) {
                    accountType = 'Agency';
                } else if (state.user.masterAgencyId) {
                    accountType = 'Master Agency';
                } else {
                    accountType = 'Organization';
                }
                ;

                window.Intercom('update', {
                    app_id: APP_ID,
                    user_id: state.user.id || null,
                    name: (state.user.firstName || '') + ' ' + (state.user.lastName || ''),
                    email: state.user.email || '',
                    account_type: accountType,
                    plan_type: state.user.paidAccount ? 'Paying User' : 'Free User',
                    client_id: state.user.clientId,
                    agency_id: state.user.agencyId,
                    master_agency_id: state.user.masterAgencyId,
                    promo_name: state.user.client ? state.user.client.promoName : null, // e.g. Vendor 1 Free User
                    first_vendor_connection: state.user.client ? state.user.client.firstVendorConnection : null,
                    primary_vendor_connection: state.user.client ? state.user.client.primaryVendorConnection : null,
                    signup_source: state.user.client ? state.user.client.signupSource : null,
                });

                resolve();
            });
        },

        logout({ dispatch, commit }) {
            return new Promise(resolve => {
                commit('clearUser');
                commit('clearMasterAgencyContext');
                commit('clearAgencyContext');
                commit('clearVendorContext');
                commit('clearClientContext');
                commit('clearAccountContext');
                dispatch('clearIntercomConfiguration');
                commit('setLoggedInStatus', false);
                commit('clearLoginDestinationRoute');
                commit('clearAccountPrivacy')
                dispatch('theme/unloadTheme');
                dispatch('woli/unloadUserConfig');
                resolve();
            });
        },

        clearIntercomConfiguration() {
            return new Promise(resolve => {
                window.Intercom('update', {
                    app_id: APP_ID,
                    user_id: null,
                    name: null,
                    email: null,
                    plan_type: null,
                    account_type: null,
                    client_id: null,
                    agency_id: null,
                    master_agency_id: null,
                    promo_name: null,
                    first_vendor_connection: null,
                    primary_vendor_connection: null,
                    signup_source: null,
                });

                resolve();
            });
        },

        setMasterAgencyContext({ commit, state, dispatch }, masterAgencyId) {
            return new Promise((resolve, reject) => {
                api
                    .get(`/service/MasterAgencies?$filter=Id eq ${masterAgencyId}`)
                    .then(response => {
                        commit('hydrateMasterAgencyContext', response.data.value[0]);

                        if (state.context.client.id === null && state.context.agency.id === null) {
                            dispatch('setAccountIdByMasterAgencyId', masterAgencyId).then(resolve);
                        } else {
                            resolve();
                        }
                    })
                    .catch(reject);
            });
        },

        setAgencyContext({ commit, state, dispatch }, agencyId) {
            return new Promise((resolve, reject) => {
                api.get(`/service/Agencies?$filter=Id eq ${agencyId}`)
                    .then(response => {
                        commit('hydrateAgencyContext', response.data.value[0]);
                        if (state.context.client.id === null) {
                            dispatch('setAccountIdByAgencyId', agencyId).then(resolve);
                        } else {
                            resolve();
                        }
                    })
                    .catch(reject);
            });
        },

        setAgencyContextFromRouter({ commit, state, dispatch }, payload) {
            return new Promise((resolve, reject) => {
                api.get(`/service/Agencies?$filter=Id eq ${payload.agencyId}`)
                    .then(response => {
                        commit('hydrateAgencyContext', response.data.value[0]);

                        const hasClientIdInUrl = payload.to?.params?.clientId;

                        if (!hasClientIdInUrl) {
                            dispatch('setAccountIdByAgencyId', payload.agencyId).then(resolve);
                        } else {
                            resolve();
                        }
                    })
                    .catch(reject);
            });
        },

        setClientContext({ commit, state, dispatch }, clientId) {
            return new Promise((resolve, reject) => {
                if (clientId != state.context.client.id) {
                    api.get(`/service/Clients?$filter=Id eq ${clientId}&$expand=Promo($expand=FeatureToggles($select=FeatureId,Enabled))`)
                        .then(response => {
                            commit('hydrateClientContext', response.data.value[0]);
                            commit('calculateProgress');
                            dispatch('setAccountIdByClientId', clientId).then(resolve);
                        })
                        .catch(reject);
                } else {
                    resolve();
                }
            });
        },

        setClientContextByContactId({ dispatch }, contactId) {
            return new Promise((resolve, reject) => {
                api
                    .get(`/service/Contacts?$filter=Id eq ${contactId}&$select=ClientId`)
                    .then(response => {
                        if (response.data.value[0].ClientId) {
                            dispatch('setClientContext', response.data.value[0].ClientId).then(resolve);
                        }
                    })
                    .catch(reject);
            });
        },

        setClientContextByCompanyId({ dispatch }, companyId) {
            return new Promise((resolve, reject) => {
                api
                    .get(`/service/Companies?$filter=Id eq ${companyId}&$select=ClientId`)
                    .then(response => {
                        if (response.data.value[0].ClientId) {
                            dispatch('setClientContext', response.data.value[0].ClientId).then(resolve);
                        }
                    })
                    .catch(reject);
            });
        },

        setClientContextBySalespersonId({ dispatch }, salesPersonId) {
            return new Promise((resolve, reject) => {
                api
                    .get(`/service/Users?$filter=Id eq '${salesPersonId}'&$select=ClientId`)
                    .then(response => {
                        if (response.data.value[0].ClientId) {
                            dispatch('setClientContext', response.data.value[0].ClientId).then(resolve);
                        }
                    })
                    .catch(reject);
            });
        },

        setClientContextByCampaignId({ dispatch }, campaignId) {
            return new Promise((resolve, reject) => {
                api
                    .get(`/service/Campaigns/?$filter=Id eq ${campaignId}&$select=ClientId`)
                    .then(response => {
                        var hasClientContext = response.data && response.data.value.length > 0 && response.data.value[0].ClientId;
                        if (!hasClientContext) {
                            reject();
                        }

                        dispatch('setClientContext', response.data.value[0].ClientId)
                            .then(resolve)
                            .catch(reject);
                    })
                    .catch(reject);
            });
        },

        setClientContextByLandingPageId({ dispatch }, landingPageId) {
            return new Promise((resolve, reject) => {
                api
                    .get(`/service/LandingPages?$filter=Id eq ${landingPageId}&$expand=Account($select=ClientId)&$select=Account`)
                    .then(response => {
                        var hasClientContext = response.data
                            && response.data.value.length > 0
                            && response.data.value[0].Account
                            && response.data.value[0].Account.ClientId;
                        if (!hasClientContext) {
                            reject();
                        }

                        dispatch('setClientContext', response.data.value[0].Account.ClientId)
                            .then(resolve)
                            .catch(reject);
                    })
                    .catch(reject);
            });
        },

        setClientContextByCampaignTemplateId({ dispatch }, campaignTemplateId) {
            return new Promise((resolve, reject) => {
                api
                    .get(`/service/CampaignTemplates/?$filter=Id eq ${campaignTemplateId}&$select=ClientId,AgencyId`)
                    .then(response => {
                        if (response.data.value[0].AgencyId) {
                            dispatch('setAgencyContext', response.data.value[0].AgencyId).then(resolve);
                        }

                        if (response.data.value[0].ClientId) {
                            dispatch('setClientContext', response.data.value[0].ClientId).then(resolve);
                        }

                        if (!response.data.value[0].AgencyId && !response.data.value[0].ClientId) {
                            resolve();
                        }
                    })
                    .catch(reject);
            });
        },

        setClientContextByListId({ dispatch }, listId) {
            return new Promise((resolve, reject) => {
                api
                    .get(`/service/Lists?$filter=Id eq ${listId}&$select=ClientId`)
                    .then(response => {
                        if (response.data.value[0].ClientId) {
                            dispatch('setClientContext', response.data.value[0].ClientId).then(resolve);
                        }
                    })
                    .catch(reject);
            });
        },

        setContextByTemplateId({ dispatch }, templateId) {
            return new Promise((resolve, reject) => {
                api
                    .get(`/service/Templates?$filter=Id eq ${templateId}&$select=ClientId,AgencyId`)
                    .then(response => {
                        if (response.data.value[0].AgencyId) {
                            dispatch('setAgencyContext', response.data.value[0].AgencyId).then(resolve);
                        }

                        if (response.data.value[0].ClientId) {
                            dispatch('setClientContext', response.data.value[0].ClientId).then(resolve);
                        }

                        if (!response.data.value[0].AgencyId && !response.data.value[0].ClientId) {
                            resolve();
                        }
                    })
                    .catch(reject);
            });
        },

        setContextByJourneyId({ dispatch }, journeyId) {
            return new Promise((resolve, reject) => {
                api
                    .get(`/service/Journeys?$filter=Id eq ${journeyId}&$select=ClientId,AgencyId`)
                    .then(response => {
                        if (response.data.value[0].AgencyId) {
                            dispatch('setAgencyContext', response.data.value[0].AgencyId).then(resolve);
                        }

                        if (response.data.value[0].ClientId) {
                            dispatch('setClientContext', response.data.value[0].ClientId).then(resolve);
                        }
                    })
                    .catch(reject);
            });
        },

        setContextByJourneyTemplateId({ dispatch }, journeyTemplateId) {
            return new Promise((resolve, reject) => {
                api
                    .get(`/service/JourneyTemplates?$filter=Id eq ${journeyTemplateId}&$select=ClientId,AgencyId`)
                    .then(response => {
                        if (response.data.value[0].AgencyId) {
                            dispatch('setAgencyContext', response.data.value[0].AgencyId).then(resolve);
                        }

                        if (response.data.value[0].ClientId) {
                            dispatch('setClientContext', response.data.value[0].ClientId).then(resolve);
                        }

                        if (!response.data.value[0].AgencyId && !response.data.value[0].ClientId) {
                            resolve();
                        }
                    })
                    .catch(reject);
            });
        },

        setContextByMarketingPlanId({ dispatch }, marketingPlanId) {
            return new Promise((resolve, reject) => {
                api
                    .get(`/service/MarketingPlans?$filter=Id eq ${marketingPlanId}&$select=ClientId,AgencyId`)
                    .then(response => {
                        
                        if (response.data.value[0].AgencyId) {
                            dispatch('setAgencyContext', response.data.value[0].AgencyId).then(resolve);
                        }

                        if (response.data.value[0].ClientId) {
                            dispatch('setClientContext', response.data.value[0].ClientId).then(resolve);
                        }
                    })
                    .catch(reject);
            });
        },

        setContextByMarketingPlanTemplateId({ dispatch }, marketingPlanTemplateId) {
            return new Promise((resolve, reject) => {
                api
                    .get(`/service/MarketingPlanTemplates?$filter=Id eq ${marketingPlanTemplateId}&$select=ClientId,AgencyId`)
                    .then(response => {
                        
                        if (response.data.value[0].AgencyId) {
                            dispatch('setAgencyContext', response.data.value[0].AgencyId).then(resolve);
                        }

                        if (response.data.value[0].ClientId) {
                            dispatch('setClientContext', response.data.value[0].ClientId).then(resolve);
                        }

                        if (!response.data.value[0].AgencyId && !response.data.value[0].ClientId) {
                            resolve();
                        }
                    })
                    .catch(reject);
            });
        },

        setContextByUserId({ dispatch }, userId) {
            return new Promise((resolve, reject) => {
                api
                    .get(`/service/Users?$filter=Id eq '${userId}'&$select=ClientId,AgencyId,MasterAgencyId`)
                    .then(response => {
                        if (response.data.value[0].MasterAgencyId) {
                            dispatch('setMasterAgencyContext', response.data.value[0].MasterAgencyId).then(resolve);
                        }

                        if (response.data.value[0].AgencyId) {
                            dispatch('setAgencyContext', response.data.value[0].AgencyId).then(resolve);
                        }

                        if (response.data.value[0].ClientId) {
                            dispatch('setClientContext', response.data.value[0].ClientId).then(resolve);
                        }

                        if (!response.data.value[0].AgencyId && !response.data.value[0].ClientId && !response.data.value[0].MasterAgencyId) {
                            resolve();
                        }
                    })
                    .catch(reject);
            });
        },

        setContextByCollateralBuildId({ dispatch }, collateralBuildId) {
            return new Promise((resolve, reject) => {
                api
                    .get(`/service/CollateralBuilds?$filter=Id eq ${collateralBuildId}&$select=ClientId,AgencyId`)
                    .then(response => {
                        if (response.data.value[0].AgencyId) {
                            dispatch('setAgencyContext', response.data.value[0].AgencyId).then(resolve);
                        } else if (response.data.value[0].ClientId) {
                            dispatch('setClientContext', response.data.value[0].ClientId).then(resolve);
                        } else {
                            resolve();
                        }
                    })
                    .catch(reject);
            });
        },

        setAccountIdByClientId({ commit }, clientId) {
            return new Promise((resolve, reject) => {
                api.get(`/api/Clients/${clientId}/Account`)
                    .then((response) => {
                        commit('hydrateAccountContext', response.data);
                        resolve();
                    })
                    .catch(() => {
                        reject();
                    });
            });
        },

        setAccountIdByAgencyId({ commit }, agencyId) {
            return new Promise((resolve, reject) => {
                api.get(`/api/Agencies/${agencyId}/Account`)
                    .then((response) => {
                        commit('hydrateAccountContext', response.data);
                        resolve();
                    })
                    .catch(() => {
                        reject();
                    });
            });
        },

        setAccountIdByMasterAgencyId({ commit }, masterAgencyId) {
            return new Promise((resolve, reject) => {
                api.get(`/api/MasterAgencies/${masterAgencyId}/Account`)
                    .then((response) => {
                        commit('hydrateAccountContext', response.data);
                        resolve();
                    })
                    .catch(() => {
                        reject();
                    });
            });
        },

        addNewNotification({ commit }, message) {
            commit('updateNewNotification', message);
        },

        getBellCount({ commit }) {
            return new Promise((resolve, reject) => {
                api.get('/ms/notifications/v1/notifications/bell-count')
                    .then((response) => {
                        commit('updateBellCount', response.data.count);
                        resolve();
                    })
                    .catch(() => {
                        reject();
                    });
            });
        },

        getNotificationsPreferences({ commit }) {
            return new Promise((resolve, reject) => {
                api.get('/ms/notifications/v1/notification-preferences')
                    .then((response) => {
                        commit('updateNotificationPreferences', response.data.data);
                        resolve();
                    })
                    .catch(() => {
                        reject();
                    });
            });
        },

        loadCurrencies({ commit }) {
            commit('setCurrenciesLoading', true);

            return new Promise((resolve, reject) =>
                CurrencyService.getAllCurrencies()
                    .then(({ data: { data } }) => {
                        const currencies = data.map((item) => ({
                            id: item.Id,
                            name: item.Name,
                            symbol: item.Symbol,
                            isoCode: item.ISOCode
                        }));

                        commit('updateCurrencies', currencies);
                        resolve();
                    })
                    .catch((e) => {
                        reject(e);
                    })
                    .finally(() => {
                        commit('setCurrenciesLoading', false);
                    })
            );
        },
    },
    modules: {
        woli,
        theme,
        appointmentRequests

    }
});

export default store;
